import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { candidateService } from '../services/candidateService';
import Layout from '../components/Layout';
import PasswordChangeModal from '../components/PasswordChangeModal';
import CandidateDrawerClassic from '../components/CandidateDrawerClassic';
import ScopeFilterBar from '../components/ScopeFilterBar';

// Candidate Graph — CLASSIC.
//
// The pre-redesign table, kept alive on fit-v2.0 data so the two presentations
// can be compared on identical rows: /candidates shows buckets and evidence
// sentences, /candidates/classic shows the score and the factor bars.
//
// Same columns, same score badge, same drawer-on-row-click as the original. Two
// deliberate departures, both because the original controls read fields that do
// not exist any more:
//
//   - The filter rail was Sector / Role Family / Geography / Last Touch. Those
//     came from columns that were NULL on all 6,014 V0 rows, so the dropdowns
//     were permanently empty. Bucket and name search replace them.
//   - The drawer drew five factor bars; fit-v2.0 has four factors and a
//     multiplier. Reproducing five would mean inventing one, which is the exact
//     defect this model was written to remove.
//
// The desk bar is NOT one of those departures. apiCandidatesClassic calls
// scopeFor exactly as apiCandidates does, so this table has always been filtered
// to the recruiter's desk server-side -- it just said nothing about it (29 rows
// of 73,895 on a Singapore + Technology desk). A dead control is not worth
// reproducing; a LIVE filter the user cannot see or step past is the thing this
// feature set out to remove.

const PAGE_SIZE = 50;

const BUCKET_OPTIONS = [
  { value: '', label: 'All Buckets' },
  { value: 'call_now', label: 'Call now' },
  { value: 'warm', label: 'Warm' },
  { value: 'track', label: 'Track' },
  { value: 'low', label: 'Background' },
];

export default function CandidateGraphClassic() {
  const [user, setUser] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [scoringConfig, setScoringConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [bucket, setBucket] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const [scope, setScope] = useState(null);
  // Component state and nothing else -- no localStorage, no URL parameter the
  // browser restores. Every visit opens on the desk, so one click last month
  // cannot leave a recruiter in the firehose for weeks. Same rule as the
  // evidence view, deliberately: the two presentations must not disagree about
  // which rows a recruiter is looking at.
  const [dims, setDims] = useState('both');

  const loadUserData = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      if (!userData) {
        window.location.href = `${window.location.origin}/login`;
        return;
      }
      setUser(userData);
      if (userData.must_change_password) setShowPasswordModal(true);
    } catch (e) {
      window.location.href = `${window.location.origin}/login`;
    }
  }, []);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  useEffect(() => {
    let live = true;
    authService
      .getMyScope()
      .then((s) => live && setScope(s))
      // A desk that fails to load must not take the page down with it: the list
      // still works, it is just unscoped, which is the safe direction to fail.
      .catch(() => live && setScope(null));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await candidateService.getCandidatesClassic({
        bucket,
        search: query,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        dims,
      });
      setCandidates(data.candidates || []);
      setTotal(data.total || 0);
      setScoringConfig(data.scoring_config || null);
    } catch (e) {
      setError(e.message || 'Failed to load candidates');
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [bucket, query, page, dims]);

  useEffect(() => {
    if (user && !user.must_change_password) loadCandidates();
  }, [user, loadCandidates]);

  // The original coloured the badge against hardcoded 80/60 fallbacks. It now
  // uses the ACTIVE model's own thresholds, so the colour here and the bucket
  // chip on the redesigned page can never disagree about the same person.
  const getFitScoreTextColor = (score) => {
    const callNow = scoringConfig?.call_now ?? 60;
    const warm = scoringConfig?.warm ?? 35;
    if (score >= callNow) return 'text-emerald-700';
    if (score >= warm) return 'text-amber-700';
    return 'text-gray-700';
  };

  const getFitScoreBgColor = (score) => {
    const callNow = scoringConfig?.call_now ?? 60;
    const warm = scoringConfig?.warm ?? 35;
    if (score >= callNow) return 'bg-emerald-100';
    if (score >= warm) return 'bg-amber-100';
    return 'bg-gray-100';
  };

  const handleRowClick = async (candidate) => {
    setSelectedCandidate(candidate);
    setDrawerOpen(true);
    try {
      const detail = await candidateService.getCandidateClassicProfile(candidate.person_id);
      setSelectedCandidate(detail);
    } catch (e) {
      /* the row already on screen stays */
    }
  };

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <Layout user={user}>
      {showPasswordModal && (
        <PasswordChangeModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          onSuccess={() => {
            setShowPasswordModal(false);
            loadUserData();
          }}
        />
      )}

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Candidate Graph</h1>
          <p className="text-sm text-gray-500 mt-1">
            Ranked by fit score
            {scoringConfig?.model_version && (
              <span className="text-gray-400"> · {scoringConfig.model_version}</span>
            )}
          </p>
        </div>
        <a
          href="/candidates"
          data-testid="switch-to-evidence"
          className="shrink-0 text-sm text-[#1B3A6B] hover:underline whitespace-nowrap mt-1"
        >
          Switch to evidence view →
        </a>
      </div>

      <ScopeFilterBar scope={scope} dims={dims} onChange={setDims} />

      {/* Filter Rail */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label
              className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2"
              htmlFor="classic-bucket"
            >
              Bucket
            </label>
            <select
              id="classic-bucket"
              data-testid="classic-bucket"
              value={bucket}
              onChange={(e) => {
                setBucket(e.target.value);
                setPage(0);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-[#1B3A6B] text-sm"
            >
              {BUCKET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <label
              className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2"
              htmlFor="classic-search"
            >
              Candidate Name
            </label>
            <input
              id="classic-search"
              data-testid="classic-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-[#1B3A6B] text-sm"
            />
          </div>
        </div>
      </div>

      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6"
          data-testid="classic-error"
        >
          {error}
        </div>
      )}

      {/* Candidates Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-hidden">
          <table className="w-full table-fixed divide-y divide-gray-200" data-testid="classic-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-[22%] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate Name
                </th>
                <th className="w-[22%] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Employer &amp; Title
                </th>
                <th className="w-[26%] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Placement History
                </th>
                <th className="w-[10%] px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fit Score &darr;
                </th>
                <th className="w-[12%] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provenance
                </th>
                <th className="w-[8%] px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {candidates.map((candidate) => {
                const fitScore = candidate.fit_score || 0;
                const placement = (candidate.placements || [])[0];
                const more = Math.max(0, (candidate.placements || []).length - 1);

                return (
                  <tr
                    key={candidate.person_id}
                    data-testid="classic-row"
                    onClick={() => handleRowClick(candidate)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    {/* Candidate Name */}
                    <td className="px-3 py-3 align-top">
                      <div className="text-sm font-medium text-gray-900 truncate" title={candidate.full_name}>
                        {candidate.full_name}
                      </div>
                      {candidate.local_name && (
                        <div className="text-xs text-gray-500 mt-0.5 truncate" title={candidate.local_name}>
                          {candidate.local_name}
                        </div>
                      )}
                    </td>

                    {/* Current Employer & Title */}
                    <td className="px-3 py-3 align-top">
                      <div
                        className="text-sm font-semibold text-gray-900 truncate"
                        title={candidate.current_company || '-'}
                      >
                        {candidate.current_company || '-'}
                      </div>
                      <div
                        className="text-xs text-gray-500 mt-0.5 truncate"
                        title={candidate.current_title || 'Unknown Title'}
                      >
                        {candidate.current_title || 'Unknown Title'}
                      </div>
                    </td>

                    {/* Placement History */}
                    <td className="px-3 py-3 align-top">
                      {placement ? (
                        <div className="text-sm min-w-0">
                          <span className="text-gray-900 block truncate" title={placement.company}>
                            {placement.company}
                            {more > 0 && <span className="text-gray-400"> +{more} more</span>}
                          </span>
                          {placement.hiring_manager && (
                            <div
                              className="text-gray-500 text-xs mt-1 truncate"
                              title={`HM: ${placement.hiring_manager}`}
                            >
                              HM: {placement.hiring_manager}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>

                    {/* Fit Score */}
                    <td className="px-3 py-3 align-top">
                      <div className="flex justify-center">
                        <div
                          data-testid="classic-fit-score"
                          className={`w-10 h-10 rounded-xl ${getFitScoreBgColor(fitScore)} flex items-center justify-center`}
                        >
                          <span className={`text-sm font-bold ${getFitScoreTextColor(fitScore)}`}>{fitScore}</span>
                        </div>
                      </div>
                    </td>

                    {/* Provenance */}
                    <td className="px-3 py-3 align-top">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {(candidate.provenance || []).map((source) => (
                          <div
                            key={source}
                            className="flex items-center space-x-1 px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-full text-[11px] text-gray-600"
                          >
                            <svg
                              className="w-3 h-3 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
                              />
                            </svg>
                            <span className="capitalize">{source === 'ats' ? 'ATS' : source}</span>
                          </div>
                        ))}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-3 align-top">
                      <div className="flex items-center justify-center gap-2 text-gray-400">
                        {candidate.channels?.email && (
                          <svg className="w-4 h-4 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        )}
                        {candidate.channels?.phone && (
                          <svg className="w-4 h-4 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {candidates.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500" data-testid="classic-empty">
              No candidates found. Try adjusting your filters.
            </div>
          )}
          {loading && (
            <div className="text-center py-12 text-gray-500" data-testid="classic-loading">
              Loading candidates…
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 text-sm text-gray-600">
        <span data-testid="classic-total">{total.toLocaleString()} candidates</span>
        <div className="flex items-center gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40"
          >
            Previous
          </button>
          <span>
            Page {page + 1} of {lastPage + 1}
          </span>
          <button
            disabled={page >= lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      <CandidateDrawerClassic
        candidate={selectedCandidate}
        isOpen={drawerOpen}
        scoringConfig={scoringConfig}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedCandidate(null);
        }}
      />
    </Layout>
  );
}
